/* A connection check the person holding the device can read out.
 *
 * WHY THIS EXISTS
 * Sign-in failed on Safari and worked in Chrome, and the whole diagnosis
 * happened by reading a server log on a laptop and guessing. That loop is slow
 * and it puts the founder in the position of relaying symptoms. The device
 * knows exactly what went wrong; it just had no way to say so.
 *
 * HOW IT TELLS TLS FROM CORS, WHICH IS THE POINT
 * A failed fetch throws the same opaque TypeError for both, so one request
 * cannot distinguish them. Two can:
 *
 *   `no-cors` skips CORS entirely but still needs the TLS handshake to succeed.
 *   `cors` needs both.
 *
 * So no-cors passing while cors fails means the certificate is fine and the
 * problem is a CORS header. Both failing means the connection never got that
 * far: an untrusted certificate, the wrong address, or nothing listening.
 *
 * DEVELOPMENT ONLY. Mounted behind a flag the app sets from its environment,
 * because this prints internal addresses and is a support tool, not a feature.
 */
import { useState } from "react";

type Probe = {
  name: string;
  url: string;
  cors: string;
  noCors: string;
  reading: string;
};

async function attempt(url: string, mode: RequestMode): Promise<string> {
  try {
    const res = await fetch(url, { mode, cache: "no-store" });
    // An opaque response has no readable status, and that is expected: it is
    // proof the request completed, which is all this needs.
    return mode === "no-cors" ? "reached" : `HTTP ${res.status}`;
  } catch (err) {
    return err instanceof Error ? `${err.name}: ${err.message}` : "failed";
  }
}

function reading(cors: string, noCors: string): string {
  const corsOk = cors.startsWith("HTTP");
  const noCorsOk = noCors === "reached";
  if (corsOk) return "fine";
  if (noCorsOk) return "reachable, but the CORS headers are being rejected";
  return "cannot connect at all: certificate not trusted, wrong address, or nothing listening";
}

export function Diagnostics({
  apiUrl,
  authUrl,
}: {
  /** The API base including /v1, as the app uses it. */
  apiUrl: string;
  authUrl: string;
}) {
  const [open, setOpen] = useState(false);
  const [probes, setProbes] = useState<Probe[] | null>(null);
  const [busy, setBusy] = useState(false);

  // The API's health endpoint sits above the version prefix.
  const apiHealth = `${apiUrl.replace(/\/v1\/?$/, "")}/health`;
  const authHealth = `${authUrl}/health`;

  async function run() {
    setBusy(true);
    const targets = [
      { name: "API", url: apiHealth },
      { name: "Sign-in service", url: authHealth },
    ];
    const results: Probe[] = [];
    for (const t of targets) {
      const cors = await attempt(t.url, "cors");
      const noCors = await attempt(t.url, "no-cors");
      results.push({ ...t, cors, noCors, reading: reading(cors, noCors) });
    }
    setProbes(results);
    setBusy(false);
  }

  if (!open) {
    return (
      <button className="dg-diag-open" onClick={() => { setOpen(true); void run(); }}>
        Connection check
      </button>
    );
  }

  return (
    <section className="dg-diag">
      <div className="dg-diag-head">
        <span className="label">Connection check</span>
        <button className="dg-diag-open" onClick={() => setOpen(false)}>Hide</button>
      </div>

      <dl className="dg-diag-facts">
        <dt>This page</dt>
        <dd>{typeof window === "undefined" ? "-" : window.location.origin}</dd>
        <dt>Secure context</dt>
        <dd>
          {typeof window !== "undefined" && window.isSecureContext
            ? "yes"
            : "NO, so there is no service worker and no install"}
        </dd>
      </dl>

      {busy && <p className="dg-diag-note">Checking…</p>}

      {probes?.map((p) => (
        <div className="dg-diag-probe" key={p.name} data-ok={p.reading === "fine" ? "" : undefined}>
          <p className="dg-diag-name">{p.name}</p>
          <p className="dg-diag-url">{p.url}</p>
          <p className="dg-diag-reading">{p.reading}</p>
          <p className="dg-diag-raw">with CORS: {p.cors}</p>
          <p className="dg-diag-raw">without CORS: {p.noCors}</p>
        </div>
      ))}

      {probes && (
        <button className="dg-diag-open" onClick={() => void run()} disabled={busy}>
          Run again
        </button>
      )}
    </section>
  );
}

export const diagnosticsCss = `
.dg-diag-open {
  align-self: start;
  border: 0; background: none; padding: 8px 0;
  color: var(--ink-faint);
  font-family: var(--font-mono);
  font-size: var(--text-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  cursor: pointer;
}
.dg-diag-open:active { color: var(--seal); }

.dg-diag {
  display: grid;
  gap: 12px;
  margin-top: 8px;
  padding: 16px 18px;
  background: var(--panel-inset, var(--paper-card));
  border: 1px solid var(--border);
  border-radius: var(--radius-card, 14px);
  /* Long URLs and error strings must wrap rather than push the page sideways. */
  overflow-wrap: anywhere;
}
.dg-diag-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

.dg-diag-facts {
  display: grid; grid-template-columns: auto 1fr; gap: 4px 14px; margin: 0;
  font-size: var(--text-small);
}
.dg-diag-facts dt { color: var(--ink-faint); }
.dg-diag-facts dd { margin: 0; font-family: var(--font-mono); font-size: 11.5px; }

.dg-diag-probe {
  display: grid; gap: 2px;
  padding-top: 12px; border-top: 1px solid var(--border-soft);
}
.dg-diag-name { margin: 0; font-weight: 600; font-size: var(--text-small); }
.dg-diag-url { margin: 0; font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); }
.dg-diag-reading { margin: 2px 0 4px; font-size: var(--text-small); color: var(--danger); }
.dg-diag-probe[data-ok] .dg-diag-reading { color: var(--success); }
.dg-diag-raw { margin: 0; font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); }
.dg-diag-note { margin: 0; color: var(--ink-soft); font-size: var(--text-small); }
`;
